// © Microsoft Corporation. All rights reserved.

import { groupBy, identity } from 'lodash-es';

import { PageMeasure } from "./PerfTimeline";
import { getExperienceMeasures } from './ExperienceDiagnostics';
import { getPageTimings } from './PageTimings';

/** The type that encapsulates the data for a single aggregated timeline object */
type Timeline = {

    /** Name of the timeline */
    name: string,

    /** The data for the timeline */
    measures: Array<PageMeasure>
};

/** Type for easily looking up if a given timeline should be considered async */
type AsyncTimelineGroups = {
    [timelineName: string]: {
        /** process ID from the async group */
        pid: number
    }
};

/**
 * Calculates and returns the JSON that can be used in the chrome://tracing tool to render an
 * experience-centric perf timeline for various tracked page measurements in the pageMeasures object.
 * See https://docs.google.com/document/d/1CvAClvFfyA5R-PhYUmn5OOQtYMH4h6I0nSsKchNAySU/preview# for more details.
 *
 * @returns Array object with chrome trace event data that can be serialized and saved to a JSON file
 *      to be loaded inside the chrome://tracing tool.
 */
export function getAppPerfTrace(): Array<any> {

    // create a shallow copy since we don't want to mutate the original data from the page object
    const measures: Array<PageMeasure> = [];

    // The async process ID counter, which will be used to group similar timelines under virtual process IDs to
    // identify and group a list of related timelines. The timeline groups will render in order of smallest to
    // larges process ID in the chrome trace.
    let asyncPID = 0;

    const asyncExperiences = {

        // Web-Worker
        "WW": { pid: asyncPID++ },

        // Config resolver on the render thread
        "Resolver": { pid: asyncPID++ }
    };

    // this is the process ID which will identify the main timeline (that renders last in the tool)
    const defaultPID = 100;

    // merge experience timings
    measures.push(...getExperienceMeasures());

    // merge resource timings
    mergeResourceTimings(measures, asyncExperiences, asyncPID);

    // merge other page timings
    const pageTimings = { ...getPageTimings() };
    mergePageTimings(measures, pageTimings, /^WW\./, "WW");

    // merge timings from window._appTimings object if it exists (used to get timeline measurements
    // out of band from non-typescript enabled code)
    const appTimings = { ...(window._appTimings || {}) };
    mergePageTimings(measures, appTimings, /.*/);

    // build chrome trace object
    const chromeTrace = [];

    // zero start instance marker to indicate nav start
    chromeTrace.push(chromeInstantMarker("Global", "NavStart", defaultPID, 0, "g"));

    // Other well known instance markers
    Object.keys(pageTimings)
        .filter(name => name.match(/TT/))
        .sort((key1, key2) => pageTimings[key1] - pageTimings[key2])
        .forEach(name =>
            chromeTrace.push(chromeInstantMarker("Global", name, defaultPID, pageTimings[name], "g"))
        );

    // web-worker instant markers
    Object.keys(pageTimings)
        .filter(name => name.match(/^WW\./i))
        .sort((key1, key2) => pageTimings[key1] - pageTimings[key2])
        .forEach(name =>
            chromeTrace.push(chromeInstantMarker("WW", name, asyncExperiences["WW"].pid, pageTimings[name], "p"))
        );

    // Main process name metadata
    chromeTrace.push(chromeMetadata("process_name", defaultPID, { name: "Main" }));
    chromeTrace.push(chromeMetadata("process_sort_index", defaultPID, { sort_index: defaultPID }));

    const timelineList = calculateTimelines(measures);
    timelineList.forEach((timeline, timelineIndex) => {

        const asyncExperienceInfo = asyncExperiences[timeline.name];
        const pid = asyncExperienceInfo
            ? asyncExperienceInfo.pid
            : defaultPID;

        // if we encountered an async timeline, we want to create it as a separate process
        // so the individual measurements can start on their own line as async events should
        if (asyncExperienceInfo) {

            timeline.measures.forEach((measure, measureIndex) => {

                const { timelineName, measureName, from, to } = measure;
                const measureIndexPrefix = ("000" + measureIndex).substr(-3, 3);
                const name = `${measureIndexPrefix}-${measureName}`;

                // async begin/end
                chromeTrace.push(...chromeAsyncMeasure(timelineName, name, pid, timelineIndex, measureIndex, from, to));
            });

            // since we're creating a new process id for async experiences, put the name metadata for the process
            chromeTrace.push(chromeMetadata("process_name", pid, { name: timeline.name }));
            chromeTrace.push(chromeMetadata("process_sort_index", pid, { sort_index: pid }));

        } else {

            timeline.measures.forEach(measure => {
                const { timelineName, measureName, from, to } = measure;
                chromeTrace.push(chromeSyncMeasure(timelineName, measureName, pid, timelineIndex, from, to));
                // TODO: if a measure.async is true, need to be able to show that within the main process without
                //      overlapping. This is currently happening with config & script measures that overlap with each other.
            });
        }

        chromeTrace.push(chromeMetadata("thread_name", pid, { name: timeline.name }, timelineIndex));
        chromeTrace.push(chromeMetadata("thread_sort_index", pid, { sort_index: timelineIndex }, timelineIndex));
    });

    return chromeTrace;
}

/**
 * Creates a chrome metadata entry object
 */
function chromeMetadata(metadataType: string, pid: number, args: object, threadId?: number): object {

    const metadata: any = {
        name: metadataType,
        ph: "M",
        pid,
        args
    };

    if (threadId != null) {
        metadata.tid = threadId;
    }

    return metadata;
}

/**
 * Returns two markers that signifiy the measurement of an asynchronous event
 */
function chromeAsyncMeasure(category: string, name: string, pid: number, threadId: number, id: number, fromMs: number, toMs: number, showInParentThread?: boolean): Array<object> {
    return [
        chromeMarker(category, name, showInParentThread ? "B" : "b", pid, threadId, fromMs, { id }),
        chromeMarker(category, name, showInParentThread ? "E" : "e", pid, threadId, toMs, { id })
    ];
}

/**
 * Returns two markers that signifiy the measurement of an synchronous event
 */
function chromeSyncMeasure(category: string, name: string, pid: number, threadId: number, fromMs: number, toMs: number): object {
    return chromeMarker(category, name, "X", pid, threadId, fromMs, { dur: (toMs - fromMs) * 1000 });
}

/**
 * Returns an instance marker
 */
function chromeInstantMarker(category: string, name: string, pid: number, timeStampMs: number, type: string): object {
    return chromeMarker(category, name, "i", pid, null, timeStampMs, { s: type });
}

/**
 * Creates a generic Chrome marker entry
 */
function chromeMarker(category: string, name: string, type: string, pid: number, threadId: number = null, timeStampMs: number, auxData: any): object {
    const marker: any = {
        cat: category,
        name,
        ph: type,
        pid,
        tid: threadId,
        ts: timeStampMs * 1000,
        ...auxData
    };

    if (threadId != null) {
        marker.tid = threadId;
    }

    return marker;
}

/**
 * Iterates through the given measure data, parses, groups timeline data and creates
 * a sorted array of {Timeline} objects to render in the Chrome trace
 *
 * @param measures - the measure data object to which to append new measurements.
 * @returns the array of aggregated, sorted {Timeline} objects.
 */
function calculateTimelines(measures: Array<PageMeasure>): Array<Timeline> {

    // group info objects by experience name
    const timelineGroups = groupBy(measures, info => info.timelineName);

    // create a list of sub-timelines where each sub-timeline is sorted by metric value
    const timelineList: Array<Timeline> = [];
    let maxValue = 0;

    Object.keys(timelineGroups).forEach(groupName => {

        // sort each timeline by start metric value
        const timeline = timelineGroups[groupName].sort(
            (info1, info2) => info1.from - info2.from
        );

        // get the running max value from the last metric on each sorted timeline
        maxValue = Math.max(maxValue, timeline[timeline.length - 1].to);

        timelineList.push({
            name: groupName,
            measures: timeline
        });
    });

    // sort the list of timelines by first appearing metric for each timeline.
    // This is the order we will render the timeline view from top to bottom,
    // so that each line represents a single experience's timeline.
    return timelineList.sort(
        (timeline1, timeline2) => timeline1.measures[0].from - timeline2.measures[0].from
    );
}

/**
 * Attempts to map measures from the given object bag that contains related start/end markers
 * and adds to the given measures array.
 * @param measures - the measure data object to which to append new measurements.
 * @param pageTimings - the page timings object from which to parse data from.
 * @param timelineName - Optional name to use for the timeline. If not specified the measure name will
 *  be parsed to attempt to derive the timeline name dynamically.
 */
function mergePageTimings(measures: Array<PageMeasure>, pageTimings: object, matchRegex: RegExp, timelineName?: string): void {

    // work out other WW markers
    const startSuffixRegex = /\-?[Ss]tart$/;

    // we have a range of start/end marker styles in WW code :-/
    const endSuffixLookup = {
        "-Start": "-End",
        "-start": "-end",
        "Start": "End",
        "start": "end"
    };

    const processedKeysToRemove = [];

    Object.keys(pageTimings)
        .filter(key => key.match(matchRegex))
        .forEach(key => {

            const startSuffixMatch = key.match(startSuffixRegex);
            if (!startSuffixMatch) {
                return;
            }

            let measureName = key.replace(startSuffixRegex, "");
            const endKey = `${measureName}${endSuffixLookup[startSuffixMatch[0]]}`;
            const endValue = startSuffixMatch && pageTimings[endKey];

            if (!endValue) {
                return;
            }

            processedKeysToRemove.push(key);
            processedKeysToRemove.push(endKey);

            let thisTimelineName = timelineName;

            // if we're not given an explicit timeline name, then see if we can try to derive the
            // timeline name from the measurement (i.e., see if the measure name is formatted as
            // [timeline name].[measure name]), and if so, use that instead. This is useful for aggregating
            // related timeline measures across code that use the PerfTimeline class as well as the global
            // window._appTimings object to generate a unified timeline.
            if (!thisTimelineName) {
                const prefixSuffixMatch = measureName.match(/(.+)\.(.+)/);
                if (prefixSuffixMatch) {
                    thisTimelineName = prefixSuffixMatch[1];
                    measureName = prefixSuffixMatch[2];
                } else {
                    // no explicit timeline name, and no timeline name could be derived from the measure name
                    return;
                }
            }

            measures.push({
                timelineName: thisTimelineName,
                measureName,
                from: pageTimings[key],
                to: endValue
            });
        });

    // remove the keys we used for detecting start/end from the measures so we don't attempt
    // to process them again for something else
    for (const keyToRemove of processedKeysToRemove) {
        delete pageTimings[keyToRemove];
    }
}

/**
 * Parses various resource timings that are of interest and merges into the measure data object
 * @returns {number} The next usable async PID
 */
function mergeResourceTimings(measures: Array<PageMeasure>, asyncExperiences: AsyncTimelineGroups, asyncPID: number): number {

    // interesting groups from resource timings
    let resourceGroupings = [

        // Various known service calls to 1S (i.e., feed, structure data, etc.)
        { name: "Services", regex: /\/service\// },

        // Config service calls (CRS or Config)
        { name: "Configs", regex: /\/config/ },

        // Content service calls (i.e., content preview)
        { name: "Content", regex: /\/content\// },

        // Javascript resources
        { name: "JS", regex: /\.js/ }
    ];

    const nonEmptyResources = performance.getEntriesByType("resource")
        .filter(entry => entry.duration > 0);

    resourceGroupings.forEach(resourceGroup => {

        // add the resource group names to the async experiences list
        asyncExperiences[resourceGroup.name] = {
            pid: asyncPID++
        };

        nonEmptyResources
            .filter(entry => entry.name.match(resourceGroup.regex))
            .forEach((entry, entryIndex) => {

                const url = new URL(entry.name);

                measures.push({
                    timelineName: resourceGroup.name,
                    measureName: `${url.pathname}-${entryIndex}`,
                    from: entry.startTime,
                    to: entry.startTime + entry.duration
                });
            });
    });

    return asyncPID;
}